import {skipToken} from '@reduxjs/toolkit/query';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  closeDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  openDialog,
  showNotification,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {
  WarrantyProgramRequestBody,
  useCreateWarrantyProgramMutation,
  useGetWarrantyProgramQuery,
  useGetWatermarkListQuery,
  useUpdateWarrantyProgramMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {getAvailableLocales, handleApiError, useTenant} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {openRepublishInfoDialog} from '../../../components/RepublishInfoDialogContent/utils/openRepublishInfoDialog';
import {WARRANTY_PROGRAM_FORM_DIALOG_ID} from '../constants/WARRANTY_PROGRAM_FORM_DIALOG_ID';
import {
  warrantyProgramFormSchema,
  WarrantyProgramFormValues,
} from '../schemas/warrantyProgramFormSchema';
import {buildTermsAndConditionsRecord} from '../utils/buildTermsAndConditionsRecord';
import {buildTermsAndConditionsRequest} from '../utils/buildTermsAndConditionsRequest';
import {getTermsAndConditionsLabel} from '../utils/getTermsAndConditionsLabel';
import {WarrantyRepublishDialog} from './WarrantyRepublishDialog';

const WARRANTY_REPUBLISH_DIALOG_ID = 'warrantyProgramRepublishDialog';

interface WarrantyProgramFormProps extends RequiredTestIdProps {
  warrantyProgramId?: string;
}

export function WarrantyProgramForm(props: WarrantyProgramFormProps) {
  const warrantyProgramId = props.warrantyProgramId;
  const isEditing = isNotNil(warrantyProgramId);

  const {data: watermarks, isLoading: areWatermarksLoading} = useGetWatermarkListQuery();
  const {
    data: program,
    isLoading: isProgramLoading,
    isError: isProgramError,
  } = useGetWarrantyProgramQuery(isNotNil(warrantyProgramId) ? {warrantyProgramId} : skipToken);
  const {tenant, isLoading: isTenantLoading} = useTenant();

  const [createWarrantyProgram] = useCreateWarrantyProgramMutation();
  const [updateWarrantyProgram] = useUpdateWarrantyProgramMutation();

  const isLoading = areWatermarksLoading || isTenantLoading || (isEditing && isProgramLoading);

  const availableLocales = getAvailableLocales(tenant);

  const watermarkOptions = (watermarks ?? []).map((watermark) => ({
    label: watermark.name,
    value: watermark.id,
  }));

  const saveProgram = (
    values: WarrantyProgramFormValues,
    republishAds: boolean,
    onSaved: () => void
  ) => {
    const requestBody: WarrantyProgramRequestBody = {
      name: values.name,
      duration: values.duration,
      watermarkId: values.watermarkId,
      termsAndConditions: buildTermsAndConditionsRequest(values.termsAndConditions),
      republishAds,
    };

    const request = isNotNil(warrantyProgramId)
      ? updateWarrantyProgram({
          warrantyProgramId,
          updateWarrantyProgramRequestBody: requestBody,
        })
      : createWarrantyProgram({createWarrantyProgramRequestBody: requestBody});

    return request
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSaved'));
        onSaved();
      })
      .catch(handleApiError);
  };

  const handleRepublishChoice = (values: WarrantyProgramFormValues, republishAds: boolean) =>
    saveProgram(values, republishAds, () => {
      // The republish confirmation is stacked on top of the program form dialog,
      // so both have to be closed before the follow-up info dialog is shown.
      closeDialog(WARRANTY_REPUBLISH_DIALOG_ID);
      closeDialog(WARRANTY_PROGRAM_FORM_DIALOG_ID);

      if (republishAds) {
        openRepublishInfoDialog();
      }
    });

  const handleSubmit: FormSubmitHandler<WarrantyProgramFormValues> = async (values) => {
    const watermarkChanged =
      isEditing && (program?.watermarkId ?? null) !== (values.watermarkId ?? null);

    if (!watermarkChanged) {
      await saveProgram(values, false, () => closeDialog(WARRANTY_PROGRAM_FORM_DIALOG_ID));

      return;
    }

    openDialog(
      <WarrantyRepublishDialog
        onSubmit={(republishAds) => handleRepublishChoice(values, republishAds)}
        data-testid={suffixTestId('republish', props)}
      />,
      {
        id: WARRANTY_REPUBLISH_DIALOG_ID,
        title: i18n.t('entity.warranty.republishDialog.title'),
        'data-testid': suffixTestId('republishDialog', props),
      }
    );
  };

  return (
    <DataStatus isLoading={isLoading} isError={isProgramError} minHeight={60}>
      <Form<WarrantyProgramFormValues>
        experimentalZodSchema={warrantyProgramFormSchema}
        defaultValues={{
          name: program?.name ?? '',
          duration: program?.duration ?? null,
          watermarkId: program?.watermarkId ?? null,
          termsAndConditions: buildTermsAndConditionsRecord(
            availableLocales,
            program?.termsAndConditions
          ),
        }}
        onSubmit={handleSubmit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              type="text"
              control={control}
              name="name"
              label={i18n.t('general.labels.name')}
              data-testid={suffixTestId('name', props)}
              isRequired
            />

            <Grid columns={2}>
              <FormField
                type="number"
                control={control}
                name="duration"
                isStepperVisible
                label={i18n.t('entity.warranty.labels.defaultDuration')}
                data-testid={suffixTestId('duration', props)}
              />

              <FormField
                type="choice"
                control={control}
                name="watermarkId"
                options={watermarkOptions}
                menuInPortal
                label={i18n.t('entity.warranty.labels.watermark')}
                helperText={i18n.t('entity.warranty.labels.watermarkHelper')}
                data-testid={suffixTestId('watermarkId', props)}
              />
            </Grid>

            {availableLocales.map((locale) => (
              <FormField
                key={locale}
                type="textarea"
                control={control}
                name={`termsAndConditions.${locale}`}
                minRows={4}
                label={getTermsAndConditionsLabel(locale)}
                tooltip={i18n.t('entity.warranty.labels.termsAndConditionsTooltip')}
                data-testid={suffixTestId(`termsAndConditions-${locale}`, props)}
              />
            ))}

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discardChanges')}
                variant="secondary"
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                type="submit"
                control={control}
                title={i18n.t('general.actions.confirm')}
                data-testid={suffixTestId('confirm', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
